import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import * as apiClient from "@/services/api-client";
import type {
  AuditInputType,
  AuditListItemExtended,
  AuditSortField,
  AuditStatus,
  SortOrder,
} from "@/types";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import {
  usePollingResource,
} from "@/features/internal/hooks/usePollingResource";
import {
  getActiveTeamId,
  subscribeActiveTeam,
} from "@/services/teams/active-team";

const DEFAULT_LIMIT = 20;
const POLL_INTERVAL_MS = 3000;

type TranslationFunction = (
  key: string,
  options?: Record<string, unknown>,
) => string;

function auditIsProcessing(audit: AuditListItemExtended): boolean {
  return (
    audit.status === "QUEUED" ||
    audit.status === "PROCESSING" ||
    audit.status === "UPLOADING"
  );
}

// A snapshot of the filter/sort/page state needed to build a history list
// request. The manual (foreground) fetch and the background poll both build
// their query from a snapshot of this shape, through `loadAudits` below.
type HistoryRequestSnapshot = {
  page: number;
  sortBy: AuditSortField;
  sortOrder: SortOrder;
  statusFilter: AuditStatus | "";
  inputTypeFilter: AuditInputType | "";
  dateFrom: string;
  dateTo: string;
  debouncedSearch: string;
};

export function useAuditHistory({ t }: { t?: TranslationFunction } = {}) {
  const [statusFilter, setStatusFilter] = useState<AuditStatus | "">("");
  const [inputTypeFilter, setInputTypeFilter] = useState<
    AuditInputType | ""
  >("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortBy, setSortBy] = useState<AuditSortField>("createdAt");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [search, setSearch] = useState("");
  // 250ms so the History free-text search doesn't fire a request per
  // keystroke -- mirrors useCommandAuditSearch's debounce for the same reason.
  const debouncedSearch = useDebouncedValue(search, 250);
  // Reactive active-team (workspace) id. Switching teams changes which audits
  // belong to the current scope, so we treat it as part of the fetch identity:
  // a team change resets the view and re-requests the new scope (see the
  // team-change effect below). The storage bridge in active-team.ts keeps this
  // in sync across tabs, so the displayed scope always matches the
  // X-Team-Id the request layer will send.
  const activeTeamId = useSyncExternalStore(
    subscribeActiveTeam,
    getActiveTeamId,
    getActiveTeamId,
  );

  const [audits, setAudits] = useState<AuditListItemExtended[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const requestSeqRef = useRef(0);
  const spinnerRequestRef = useRef(0);

  // Refs for the polling fetchFn to read current filter/page state
  const paramsRef = useRef<HistoryRequestSnapshot>({
    page: 1,
    sortBy: "createdAt",
    sortOrder: "desc",
    statusFilter: "",
    inputTypeFilter: "",
    dateFrom: "",
    dateTo: "",
    debouncedSearch: "",
  });
  const auditsRef = useRef<AuditListItemExtended[]>([]);

  // Suppress the next fetchOnce call (used to skip the immediate fetchOnce
  // that usePollingResource fires on key mount and on refresh(), matching
  // the original setInterval behavior where the first poll fires after
  // POLL_INTERVAL_MS, not immediately).
  const suppressNextFetchOnceRef = useRef(true);

  // Keep refs in sync with state
  useEffect(() => {
    paramsRef.current = {
      page,
      sortBy,
      sortOrder,
      statusFilter,
      inputTypeFilter,
      dateFrom,
      dateTo,
      debouncedSearch,
    };
  }, [page, sortBy, sortOrder, statusFilter, inputTypeFilter, dateFrom, dateTo, debouncedSearch]);

  useEffect(() => {
    auditsRef.current = audits;
  }, [audits]);

  // Shared request-and-apply path for the manual (foreground) fetch and the
  // background poll: one normalization of the listAuditHistory query and one
  // application of a successful response (audits/total/error), so the two
  // callers can't drift apart. `mode` only changes spinner ownership and
  // error surfacing — background polls are silent.
  //
  // Freshness is a single mechanism: foreground calls bump requestSeqRef;
  // background calls read it without bumping. A response is discarded once
  // the sequence has advanced past the value this call saw, which covers
  // both "a newer foreground fetch superseded me" and "a filter/page change
  // fired a fresh fetchAudits while I was in flight" (every such change goes
  // through a foreground fetchAudits call, so it always bumps the sequence).
  const loadAudits = useCallback(
    async (
      snapshot: HistoryRequestSnapshot,
      mode: "foreground" | "background",
      showSpinner = false,
    ): Promise<AuditListItemExtended[] | null> => {
      const requestId = mode === "foreground"
        ? ++requestSeqRef.current
        : requestSeqRef.current;
      try {
        if (showSpinner) {
          setLoading(true);
          spinnerRequestRef.current = requestId;
        }

        const data = await apiClient.listAuditHistory({
          page: snapshot.page,
          limit: DEFAULT_LIMIT,
          sortBy: snapshot.sortBy,
          sortOrder: snapshot.sortOrder,
          status: snapshot.statusFilter || undefined,
          inputType: snapshot.inputTypeFilter || undefined,
          dateFrom: snapshot.dateFrom || undefined,
          dateTo: snapshot.dateTo || undefined,
          q: snapshot.debouncedSearch.trim() || undefined,
        });

        // A newer request has since superseded this one; discard the stale
        // response instead of letting it clobber fresher data.
        if (requestId !== requestSeqRef.current) return null;

        setAudits(data.audits);
        setTotal(data.total);
        setError(null);
        return data.audits;
      } catch (err) {
        if (requestId !== requestSeqRef.current) return null;
        if (mode === "background") {
          // Background polls fail silently: no spinner, no error banner.
          return null;
        }
        setError(
          err instanceof apiClient.ApiError
            ? err.message
            : t?.("errors.loadingFailed") ?? "Failed to load audit history",
        );
        return null;
      } finally {
        if (showSpinner && requestId === spinnerRequestRef.current) {
          setLoading(false);
        }
      }
    },
    [t],
  );

  const fetchAudits = useCallback(
    async (showLoadingSpinner = true, pageOverride?: number) => {
      await loadAudits(
        {
          page: pageOverride ?? page,
          sortBy,
          sortOrder,
          statusFilter,
          inputTypeFilter,
          dateFrom,
          dateTo,
          debouncedSearch,
        },
        "foreground",
        showLoadingSpinner,
      );
    },
    [
      page,
      sortBy,
      sortOrder,
      statusFilter,
      inputTypeFilter,
      dateFrom,
      dateTo,
      debouncedSearch,
      loadAudits,
    ],
  );

  const clearFilters = useCallback(() => {
    setStatusFilter("");
    setInputTypeFilter("");
    setDateFrom("");
    setDateTo("");
    setSearch("");
  }, []);

  // Reset to page 1 in the SAME state transition as each filter/sort change
  // (rather than via a separate effect) so only one fetch fires per
  // interaction instead of one for the stale page and one for page 1.
  const setStatusFilterAndResetPage = useCallback((value: AuditStatus | "") => {
    setStatusFilter(value);
    setPage(1);
  }, []);
  const setInputTypeFilterAndResetPage = useCallback(
    (value: AuditInputType | "") => {
      setInputTypeFilter(value);
      setPage(1);
    },
    [],
  );
  const setDateFromAndResetPage = useCallback((value: string) => {
    setDateFrom(value);
    setPage(1);
  }, []);
  const setDateToAndResetPage = useCallback((value: string) => {
    setDateTo(value);
    setPage(1);
  }, []);
  const setSortByAndResetPage = useCallback((value: AuditSortField) => {
    setSortBy(value);
    setPage(1);
  }, []);
  const setSortOrderAndResetPage = useCallback((value: SortOrder) => {
    setSortOrder(value);
    setPage(1);
  }, []);
  const setSearchAndResetPage = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  useEffect(() => {
    void fetchAudits();
  }, [fetchAudits]);
  // On a workspace (active-team) switch the list currently on screen belongs
  // to the *previous* team. Clear it and re-request page 1 for the new scope
  // so another team's rows/total never linger on screen and the bulk-delete
  // count always targets the scope now displayed. The first mount is skipped:
  // the initial load is owned by the [fetchAudits] effect above.
  const prevActiveTeamRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const prev = prevActiveTeamRef.current;
    prevActiveTeamRef.current = activeTeamId;
    if (prev === undefined || prev === activeTeamId) return;
    setAudits([]);
    setTotal(0);
    setError(null);
    setLoading(true);
    setPage(1);
    void fetchAudits(true, 1);
    // fetchAudits intentionally omitted: it is re-created on filter/page
    // changes, and we only want to react to *team* changes here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTeamId]);

  // Clamp out-of-range pages (e.g. deleting the last row on the last page)
  // back to the last valid page instead of showing a stale/empty page.
  useEffect(() => {
    const lastValidPage = Math.max(1, Math.ceil(total / DEFAULT_LIMIT));
    if (page > lastValidPage) {
      setPage(lastValidPage);
    }
  }, [total, page]);

  // Background polling via usePollingResource
  // - Runs every POLL_INTERVAL_MS while there are processing audits
  // - Stops automatically via isTerminal when no audits are processing
  // - Visibility-gated: skips actual fetch while tab is hidden, but keeps
  //   the interval ticking so timer identity doesn't change on tab switches
  const pollingFetchFn = useCallback(async () => {
    // Suppress the next fetchOnce (used for initial mount and refresh() calls)
    // to match original setInterval behavior where first poll fires after
    // POLL_INTERVAL_MS, not immediately.
    if (suppressNextFetchOnceRef.current) {
      suppressNextFetchOnceRef.current = false;
      return auditsRef.current;
    }

    // Visibility gate: skip the actual fetch while tab is hidden,
    // but return current audits so isTerminal can still evaluate them.
    // This preserves the existing behavior where the interval keeps
    // running but fetches are gated on visibility.
    if (document.visibilityState !== "visible") {
      return auditsRef.current;
    }

    // paramsRef carries the latest filter/page snapshot. loadAudits applies
    // the shared freshness check: if a foreground fetchAudits superseded
    // this poll while it was in flight, the response is dropped.
    const rows = await loadAudits(paramsRef.current, "background");
    return rows ?? auditsRef.current;
  }, [loadAudits]);

  // Polling key is always defined so usePollingResource is mounted.
  // Polling lifecycle is controlled by isTerminal (stops when no processing
  // audits) and refresh() (restarts when new processing audits appear).
  const pollingKey = "audit-history-background-poll";

  const polling = usePollingResource<AuditListItemExtended[]>(
    pollingFetchFn,
    pollingKey,
    {
      intervalMs: POLL_INTERVAL_MS,
      maxDurationMs: Number.MAX_SAFE_INTEGER, // effectively no max duration
      isTerminal: (data) => !data?.some(auditIsProcessing),
    },
  );

  // When manual fetches (initial load, filter changes, page changes) detect
  // processing audits while polling is stopped, restart it via refresh().
  // Suppress the immediate fetchOnce that refresh() triggers.
  useEffect(() => {
    if (audits.some(auditIsProcessing) && !polling.isPolling) {
      suppressNextFetchOnceRef.current = true;
      polling.refresh();
    }
  }, [audits, polling.isPolling, polling.refresh]);

  const clearAllFilters = useCallback(() => {
    clearFilters();
    setPage(1);
  }, [clearFilters]);

  const hasActiveFilters = Boolean(
    statusFilter || inputTypeFilter || dateFrom || dateTo || search,
  );

  return {
    audits,
    loading,
    error,
    page,
    total,
    limit: DEFAULT_LIMIT,
    statusFilter,
    inputTypeFilter,
    dateFrom,
    dateTo,
    sortBy,
    sortOrder,
    search,
    debouncedSearch,
    setStatusFilter: setStatusFilterAndResetPage,
    setInputTypeFilter: setInputTypeFilterAndResetPage,
    setDateFrom: setDateFromAndResetPage,
    setDateTo: setDateToAndResetPage,
    setSortBy: setSortByAndResetPage,
    setSortOrder: setSortOrderAndResetPage,
    setSearch: setSearchAndResetPage,
    clearFilters: clearAllFilters,
    hasActiveFilters,
    fetchAudits,
    setPage,
  };
}